import type {
  Edizione,
  EsitoBatch,
  EsitoVoto,
  InfoInvito,
  InfoReset,
  Sessione,
  StatoSerata,
  VotoInviato,
} from "./tipi"

/** Errore che porta con sé il codice HTTP: il 409 va distinto dagli altri
 *  perché significa "sei arrivato tardi", non "non sei autorizzato". */
export class ErroreApi extends Error {
  readonly codice: number

  constructor(codice: number, messaggio: string) {
    super(messaggio)
    this.codice = codice
  }

  /** True quando il server dice che i voti sono ormai chiusi. */
  get troppoTardi(): boolean {
    return this.codice === 409
  }
}

async function richiesta<T>(
  percorso: string,
  opzioni: RequestInit & { token?: string } = {},
): Promise<T> {
  const { token, headers, ...resto } = opzioni
  let risposta: Response
  try {
    risposta = await fetch(percorso, {
      ...resto,
      headers: {
        "content-type": "application/json",
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
    })
  } catch {
    // Rete assente: chi chiama decide se accodare o mostrare l'offline.
    throw new ErroreApi(0, "Nessuna connessione")
  }

  if (!risposta.ok) {
    throw new ErroreApi(risposta.status, await messaggioErrore(risposta))
  }
  return risposta.json() as Promise<T>
}

async function messaggioErrore(risposta: Response): Promise<string> {
  try {
    const corpo = await risposta.json()
    const dettaglio = corpo?.detail
    if (typeof dettaglio === "string") return dettaglio
    // Gli errori di validazione di FastAPI arrivano come lista.
    if (Array.isArray(dettaglio) && dettaglio[0]?.msg) return String(dettaglio[0].msg)
  } catch {
    /* corpo non leggibile: sotto c'è un messaggio generico */
  }
  return `Errore ${risposta.status}`
}

export const api = {
  invito: (joinCode: string) =>
    richiesta<InfoInvito>(`/api/auth/invito/${encodeURIComponent(joinCode)}`),

  registrati: (dati: { join_code: string; nickname: string; password: string; emoji: string }) =>
    richiesta<Sessione>("/api/auth/registrati", { method: "POST", body: JSON.stringify(dati) }),

  accedi: (dati: { nickname: string; password: string; join_code?: string }) =>
    richiesta<Sessione>("/api/auth/accedi", { method: "POST", body: JSON.stringify(dati) }),

  infoReset: (codice: string) =>
    richiesta<InfoReset>(`/api/auth/reset/${encodeURIComponent(codice)}`),

  reset: (codice: string, password: string) =>
    richiesta<{ sessione: Sessione | null }>("/api/auth/reset", {
      method: "POST",
      body: JSON.stringify({ codice, password }),
    }),

  edizione: (id: string, token: string) => richiesta<Edizione>(`/api/editions/${id}`, { token }),

  statoSerata: (serataId: string, token: string) =>
    richiesta<StatoSerata>(`/api/serate/${serataId}/stato`, { token }),

  vota: (voto: VotoInviato, token: string) =>
    richiesta<EsitoVoto>("/api/votes", { method: "PUT", body: JSON.stringify(voto), token }),

  /** Svuotamento della coda offline: i falliti non fanno fallire gli altri. */
  votaBatch: (votes: VotoInviato[], token: string) =>
    richiesta<EsitoBatch>("/api/votes/batch", {
      method: "PUT",
      body: JSON.stringify({ votes }),
      token,
    }),
}
